import React, { useEffect, useId, useMemo } from 'react';
import ReactModal from 'react-modal';

import { Props as FlexProps } from '@rover/kibble/core/Flex/Flex';
import NoScrollController from '@rover/kibble/official-patterns/Modal/utils/NoScrollController';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';

import { ButtonVariants } from '../../core/Button';
import { ButtonSizes } from '../../core/Button/Button.common';

/** Props for the row of buttons at the bottom of a modal */
export type DialogButtonProps = {
  /**
   * How the buttons line up. Defaults to `column`, with submit above cancel. On React Native, `row`
   * becomes `column` and `row-reverse` becomes `column-reverse`.
   */
  buttonDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  /**
   * Label for the cancel button, such as "Cancel". The button shows only when `onClickCancel` is
   * set too.
   */
  cancelButtonText?: string;
  /** Style of the cancel button. Defaults to `flat`. */
  cancelButtonVariant?: ButtonVariants;
  /** Called when the cancel button is pressed. The modal stays open until `isOpen` is `false`. */
  onClickCancel?: () => void;
  /** Called when the submit button is pressed. The modal stays open until `isOpen` is `false`. */
  onClickSubmit?: () => void;
  /**
   * Label for the submit button, naming the action, such as "Delete booking". The button shows
   * only when `onClickSubmit` is set too.
   */
  submitButtonText?: string;
  /** Grays out the submit button and stops it responding. Defaults to `false`. */
  submitButtonDisabled?: boolean;
  /**
   * Shows a spinner on the submit button while its action runs. Defaults to `false`. The button
   * still calls `onClickSubmit`, so guard against a second press.
   */
  submitButtonLoading?: boolean;
  /** Style of the submit button. Defaults to `primary`. */
  submitButtonVariant?: ButtonVariants;
};

/** Common props for Modal across web and native */
export type ModalCommonProps = DialogButtonProps & {
  /** The body, between the title and the buttons. */
  children: React.ReactNode;
  /** Shows the modal. */
  isOpen: boolean;
  /** Shows the ✕ in the top corner, which calls `onRequestClose`. Defaults to `false`. */
  showCloseIcon?: boolean;
  /** Size of the ✕ button. Defaults to `default`. */
  closeIconSize?: ButtonSizes;
  /**
   * Called when the reader asks to close the modal, such as with the ✕, Escape, a press on the
   * scrim or Android's Back. Set `isOpen` to `false` to close it.
   */
  onRequestClose: (payload?: unknown) => void;
  /** The title. It also names the dialog for screen readers. */
  headerText?: string;
  /** Heading size of the title. Defaults to `300`. */
  headerSize?: ThemeHeadingSizes;
  /** Alignment of the header text within its row. Centering keeps the heading semantics. */
  headerAlign?: 'left' | 'center';
  /**
   * Styles for the scrim behind the modal. They apply over the scrim's own, so they can replace its
   * color.
   */
  overlaySx?: React.CSSProperties;
  /**
   * Props for the surface that holds the title, body and buttons, applied over its own background,
   * corners, padding and widths.
   */
  contentProps?: FlexProps;
  /**
   * Props for the row that holds the title and ✕. They apply over its own layout and bottom margin.
   */
  headerProps?: FlexProps;
};

/** react-modal treats the 0×0 portal as hiding the dialog, so Tab finds nothing to focus. */
export const showPortalToFocusTrap = (options?: ReactModal.OnAfterOpenCallbackOptions): void => {
  options?.overlayEl?.parentElement?.style.setProperty('display', 'contents');
};

export const usePreventScroll = (isOpen: boolean, modalId: string): void => {
  // Prevent scrolling when modal is open
  const noScroll = new NoScrollController();
  useEffect(() => {
    if (isOpen) {
      noScroll.on(modalId);
    } else {
      noScroll.off(modalId);
    }
    return () => {
      noScroll.off(modalId);
    };
  }, [isOpen, modalId]);
};

export const useReactModalSetup = (appElementSelector: string) => {
  // setAppElement has to be called before render or else we get react-modal errors.
  // useMemo with an empty array second arg will run only once, before render, much like componentWillMount does.
  // react-modal's internals make this method safe to call server-side.
  useMemo(() => {
    ReactModal.setAppElement(appElementSelector);
  }, []);

  const staticHeaderId = useId();
  const modalId = useId();

  return { staticHeaderId, modalId };
};
